import { useMutation, useQueryClient } from '@tanstack/react-query';

import { offlineCache } from '../services/offlineCache';
import { RequestsService } from '../services/requestsService';
import { Request } from '../types';
import { RequestStage } from '../types/stages';
import {
  offlineRequestsKeys,
  useNetworkStatus,
} from './queries/useOfflineRequestsQuery';
import { requestsKeys } from './queries/useRequestsQuery';

/**
 * Close a Draft request without archiving it.
 *
 * Modelled on `useStageUpdate`, deliberately NOT on `useOfflineDeleteRequest`: the delete
 * hook removes the row from the list and detail caches, which is exactly the behaviour a
 * Draft close replaces. Here the row stays put and only its stage moves to CLOSED, so the
 * requestor still finds it in their list under the Closed filter.
 *
 * Online only, matching every other stage-writing path. There is no offline queue: a close
 * is terminal and cannot be reconciled later.
 */
export const useCloseDraftRequest = () => {
  const queryClient = useQueryClient();
  const isOnline = useNetworkStatus();

  const cacheKeys = (requestId: string) => ({
    requestDetailKey: requestsKeys.detail(requestId),
    requestsListKey: requestsKeys.lists(),
    offlineDetailKey: offlineRequestsKeys.detail(requestId),
    offlineListKey: offlineRequestsKeys.lists(),
  });

  const performOptimisticUpdate = (requestId: string) => {
    const {
      requestDetailKey,
      requestsListKey,
      offlineDetailKey,
      offlineListKey,
    } = cacheKeys(requestId);

    const previousDetailData = queryClient.getQueryData(requestDetailKey);
    const previousListData = queryClient.getQueryData(requestsListKey);
    const previousOfflineDetailData =
      queryClient.getQueryData(offlineDetailKey);
    const previousOfflineListData = queryClient.getQueryData(offlineListKey);

    const closeDetail = (oldData: Request | undefined) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        stage: RequestStage.CLOSED,
        sysRequestStage: RequestStage.CLOSED,
      };
    };

    const closeInList = (oldData: Request[] | undefined) => {
      if (!oldData) return oldData;
      return oldData.map(request =>
        request.id === requestId
          ? {
              ...request,
              stage: RequestStage.CLOSED,
              sysRequestStage: RequestStage.CLOSED,
            }
          : request,
      );
    };

    queryClient.setQueryData(requestDetailKey, closeDetail);
    queryClient.setQueryData(offlineDetailKey, closeDetail);
    queryClient.setQueryData(requestsListKey, closeInList);
    queryClient.setQueryData(offlineListKey, closeInList);

    return {
      previousDetailData,
      previousListData,
      previousOfflineDetailData,
      previousOfflineListData,
      requestDetailKey,
      requestsListKey,
      offlineDetailKey,
      offlineListKey,
    };
  };

  const rollbackOptimisticUpdate = (
    rollbackData: ReturnType<typeof performOptimisticUpdate>,
  ) => {
    queryClient.setQueryData(
      rollbackData.requestDetailKey,
      rollbackData.previousDetailData,
    );
    queryClient.setQueryData(
      rollbackData.requestsListKey,
      rollbackData.previousListData,
    );
    queryClient.setQueryData(
      rollbackData.offlineDetailKey,
      rollbackData.previousOfflineDetailData,
    );
    queryClient.setQueryData(
      rollbackData.offlineListKey,
      rollbackData.previousOfflineListData,
    );
  };

  return useMutation({
    // A close is not idempotent from the client's point of view: the second attempt hits a
    // request the server has already moved to CLOSED and comes back 400. Retrying would
    // only convert a transient failure into a confusing one.
    retry: false,
    mutationFn: async (requestId: string) => {
      if (!isOnline) {
        throw new Error(
          'Cannot close requests while offline. Please connect to internet.',
        );
      }

      const rollbackData = performOptimisticUpdate(requestId);

      try {
        const result = await RequestsService.closeDraftRequest(requestId);
        return result;
      } catch (error) {
        rollbackOptimisticUpdate(rollbackData);
        throw error;
      }
    },
    onSuccess: async (result: Request) => {
      const {
        requestDetailKey,
        requestsListKey,
        offlineDetailKey,
        offlineListKey,
      } = cacheKeys(result.id);

      // Merged rather than replaced. The close response carries the request row plus team
      // data, but not the nested repeatable-section collections the detail cache already
      // holds, so an outright replace would blank them until the next refetch.
      const mergeDetail = (oldData: Request | undefined) =>
        oldData ? { ...oldData, ...result } : result;

      const mergeInList = (oldData: Request[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(request =>
          request.id === result.id ? { ...request, ...result } : request,
        );
      };

      queryClient.setQueryData(requestDetailKey, mergeDetail);
      queryClient.setQueryData(offlineDetailKey, mergeDetail);
      queryClient.setQueryData(requestsListKey, mergeInList);
      queryClient.setQueryData(offlineListKey, mergeInList);

      // Keep the persisted offline cache in step so a reload does not resurrect DRAFT.
      try {
        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = requests.map(req =>
          req.id === result.id ? { ...req, ...result } : req,
        );
        await offlineCache.cacheRequests(updatedRequests);
      } catch (error) {
        console.error('Failed to update offline cache after close:', error);
      }

      queryClient.invalidateQueries({
        queryKey: requestsKeys.all,
        refetchType: 'active',
      });
    },
    onError: (error, requestId: string) => {
      console.error('Draft close failed:', error);

      // The rollback inside mutationFn has already put the caches back to DRAFT, which is
      // right when the write genuinely failed and wrong when the write committed but the
      // response was lost. Refetching resolves which of the two happened. Without this the
      // caches can sit at DRAFT against a CLOSED row forever, and every later action 400s.
      queryClient.invalidateQueries({
        queryKey: requestsKeys.all,
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.detail(requestId),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.lists(),
        refetchType: 'active',
      });
    },
    meta: {
      errorMessage: 'Failed to close request. Please try again.',
    },
  });
};

export default useCloseDraftRequest;
